/* Loaded only by a story video whose native autoplay setting is enabled. */
(() => {
  if (customElements.get('shopsta-story-video')) return;
  class StoryVideo extends HTMLElement {
    connectedCallback() {
      if (this.cleanup || this.dataset.autoplayVisible !== 'true') return;
      const video = this.querySelector('video');
      if (!video || !('IntersectionObserver' in window)) return;
      const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      let visible = false, near = false, manualPause = false, blocked = false;
      let pending = false, automaticPause = false, connected = true, warmed = false;
      const allowed = () => connected && !document.hidden && !motion.matches && !navigator.connection?.saveData;
      // Keep native controls as the no-JS/blocked-autoplay fallback.
      const revealControls = () => { video.controls = true; };
      const onFocus = () => { if (video.matches(':focus-visible')) revealControls(); };
      const onClick = event => {
        if (video.controls) return;
        event.preventDefault();
        revealControls();
      };
      const onKey = event => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        if (!video.controls) {
          event.preventDefault();
          revealControls();
        }
      };
      if (allowed()) video.controls = false;
      video.addEventListener('click', onClick);
      video.addEventListener('keydown', onKey);
      video.addEventListener('focus', onFocus);
      video.addEventListener('ended', revealControls);
      video.addEventListener('error', revealControls);
      const pause = () => {
        if (!video.paused) {
          automaticPause = true;
          video.pause();
        }
      };
      const warm = () => {
        if (!warmed && near && allowed()) {
          warmed = true;
          video.preload = 'metadata';
          video.load();
        }
      };
      const play = () => {
        if (!visible || !allowed() || manualPause || blocked || pending || video.ended || !video.paused) return;
        pending = true;
        // Muting is set in HTML, so later user unmuting is never overridden.
        Promise.resolve(video.play()).then(() => {
          pending = false;
          if (!visible || !allowed() || manualPause) pause();
        }).catch(error => {
          pending = false;
          if (error.name !== 'AbortError') { blocked = true; revealControls(); }
        });
      };
      const onPause = () => {
        if (automaticPause) automaticPause = false;
        else if (!video.ended) manualPause = true;
      };
      const onPlay = () => {
        if (!pending) { manualPause = false; blocked = false; }
      };
      const onVisibility = () => {
        if (motion.matches || navigator.connection?.saveData) revealControls();
        if (!allowed()) pause();
        else { warm(); play(); }
      };
      const preloadObserver = new IntersectionObserver(entries => {
        near = entries[0].isIntersecting;
        warm();
      }, {rootMargin: '300px 0px'});
      const visibilityObserver = new IntersectionObserver(entries => {
        const entry = entries[0];
        // Hysteresis avoids repeated starts/stops along a single scroll boundary.
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) visible = true;
        else if (!entry.isIntersecting || entry.intersectionRatio <= 0.1) visible = false;
        if (visible) play(); else pause();
      }, {threshold: [0, 0.1, 0.5]});
      video.addEventListener('pause', onPause);
      video.addEventListener('play', onPlay);
      document.addEventListener('visibilitychange', onVisibility);
      motion.addEventListener('change', onVisibility);
      preloadObserver.observe(video);
      visibilityObserver.observe(video);
      this.cleanup = () => {
        connected = false;
        preloadObserver.disconnect();
        visibilityObserver.disconnect();
        video.removeEventListener('click', onClick);
        video.removeEventListener('keydown', onKey);
        video.removeEventListener('focus', onFocus);
        video.removeEventListener('ended', revealControls);
        video.removeEventListener('error', revealControls);
        revealControls();
        video.removeEventListener('pause', onPause);
        video.removeEventListener('play', onPlay);
        document.removeEventListener('visibilitychange', onVisibility);
        motion.removeEventListener('change', onVisibility);
        pause();
      };
    }
    disconnectedCallback() {
      this.cleanup?.();
      this.cleanup = null;
    }
  }
  customElements.define('shopsta-story-video', StoryVideo);
})();
